import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import PriorityPopover from '../../Components/PriorityPopover/PriorityPopover'
import SchedulePopover from '../../Components/SchedulePopover/SchedulePopover'
import { SkeletonTaskList } from '../../Components/Skeleton/Skeleton'
import { useAppDispatch, useAppSelector } from '../../redux/hooks'
import {
  createTask,
  deleteTask,
  fetchTasks,
  selectTasksStatus,
  toDateKey,
  todayKey,
  updateTask,
  type Priority,
  type Task,
} from '../../redux/tasks'
import styles from './BrainDump.module.css'

const EditIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
    <path
      d="M4 20h4l10.5-10.5a2.121 2.121 0 10-3-3L5 17v3z"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path d="M13.5 6.5l3 3" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" />
  </svg>
)

const ArrowRightIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
    <path
      d="M5 12h14M13 6l6 6-6 6"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

const CalendarIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
    <rect x="3.5" y="5" width="17" height="15" rx="2.5" stroke="currentColor" strokeWidth="1.75" />
    <path d="M3.5 9.5h17" stroke="currentColor" strokeWidth="1.75" />
    <path d="M8 3.5v3M16 3.5v3" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" />
  </svg>
)

const CaretRight = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
    <path
      d="M9 6l6 6-6 6"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

const CheckIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden>
    <path
      d="M5 12l4 4L19 7"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

const TrashIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
    <path
      d="M9 3h6M4 7h16M10 11v6M14 11v6M6 7l1 14h10l1-14"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

const EyeIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
    <path
      d="M2.5 12S6 5 12 5s9.5 7 9.5 7-3.5 7-9.5 7S2.5 12 2.5 12z"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="1.75" />
  </svg>
)

const FlagIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
    <path
      d="M5 21V4M5 4h12l-2 4 2 4H5"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

const priorityEdgeClass = (priority: Priority | undefined): string => {
  switch (priority) {
    case 'p1':
      return styles.priorityEdgeP1
    case 'p2':
      return styles.priorityEdgeP2
    case 'p3':
      return styles.priorityEdgeP3
    case 'p4':
      return styles.priorityEdgeP4
    default:
      return ''
  }
}

const addDays = (d: Date, days: number) => {
  const next = new Date(d)
  next.setDate(d.getDate() + days)
  return next
}

const formatShortDate = (iso: string): string => {
  const date = new Date(iso)
  const opts: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric' }
  if (date.getFullYear() !== new Date().getFullYear()) opts.year = 'numeric'
  return new Intl.DateTimeFormat('en-US', opts).format(date)
}

const parseDateKey = (key: string): Date => {
  const [y, m, d] = key.split('-').map((n) => parseInt(n, 10))
  return new Date(y, (m ?? 1) - 1, d ?? 1)
}

type ScheduleBadgeLabel = {
  text: string
  tone: 'today' | 'scheduled' | 'overdue' | 'completed' | 'empty'
}

const scheduleLabel = (task: Task): ScheduleBadgeLabel => {
  if (task.completedAt) {
    return { text: `Completed · ${formatShortDate(task.completedAt)}`, tone: 'completed' }
  }
  if (!task.scheduledDate) return { text: '', tone: 'empty' }
  const today = todayKey()
  const tomorrow = toDateKey(addDays(new Date(), 1))
  if (task.scheduledDate === today) return { text: 'Today', tone: 'today' }
  if (task.scheduledDate === tomorrow) return { text: 'Tomorrow', tone: 'scheduled' }
  const date = parseDateKey(task.scheduledDate)
  const pretty = formatShortDate(date.toISOString())
  if (task.scheduledDate < today) return { text: `Overdue · ${pretty}`, tone: 'overdue' }
  return { text: pretty, tone: 'scheduled' }
}

const BrainDump = () => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const tasks = useAppSelector((s) => s.tasks.items)
  const tasksStatus = useAppSelector(selectTasksStatus)
  const [draft, setDraft] = useState('')
  const [openScheduleId, setOpenScheduleId] = useState<string | null>(null)
  const [openPriorityId, setOpenPriorityId] = useState<string | null>(null)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editingValue, setEditingValue] = useState('')
  const [completedOpen, setCompletedOpen] = useState(false)
  const [scheduledOpen, setScheduledOpen] = useState(true)
  const [unscheduledOpen, setUnscheduledOpen] = useState(true)

  useEffect(() => {
    void dispatch(fetchTasks())
  }, [dispatch])

  const { unscheduledTasks, scheduledTasks, completedTasks } = useMemo(() => {
    const unscheduled: Task[] = []
    const scheduled: Task[] = []
    const completed: Task[] = []
    for (const t of tasks) {
      if (t.completedAt) completed.push(t)
      else if (t.scheduledDate) scheduled.push(t)
      else unscheduled.push(t)
    }
    unscheduled.sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1))
    scheduled.sort((a, b) => {
      if (a.scheduledDate !== b.scheduledDate)
        return (a.scheduledDate ?? '') < (b.scheduledDate ?? '') ? -1 : 1
      return a.createdAt < b.createdAt ? 1 : -1
    })
    completed.sort((a, b) => ((a.completedAt ?? '') < (b.completedAt ?? '') ? 1 : -1))
    return {
      unscheduledTasks: unscheduled,
      scheduledTasks: scheduled,
      completedTasks: completed,
    }
  }, [tasks])

  const submit = () => {
    const text = draft.trim()
    if (!text) return
    void dispatch(createTask({ title: text }))
    setDraft('')
  }

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      submit()
    }
  }

  const quickAddToToday = (taskId: string) => {
    void dispatch(updateTask({ id: taskId, changes: { scheduledDate: todayKey() } }))
  }

  const reschedule = (taskId: string, dateKey: string) => {
    void dispatch(updateTask({ id: taskId, changes: { scheduledDate: dateKey } }))
    setOpenScheduleId(null)
  }

  const unschedule = (taskId: string) => {
    void dispatch(updateTask({ id: taskId, changes: { scheduledDate: null } }))
    setOpenScheduleId(null)
  }

  const setPriority = (taskId: string, priority: Priority | undefined) => {
    void dispatch(updateTask({ id: taskId, changes: { priority: priority ?? null } }))
    setOpenPriorityId(null)
  }

  const startEditing = (task: Task) => {
    setEditingId(task.id)
    setEditingValue(task.title)
    setOpenScheduleId(null)
    setOpenPriorityId(null)
  }

  const cancelEditing = () => {
    setEditingId(null)
    setEditingValue('')
  }

  const removeTask = (taskId: string, title: string) => {
    if (!window.confirm(`Delete “${title}”? This cannot be undone.`)) return
    setOpenPriorityId((prev) => (prev === taskId ? null : prev))
    setOpenScheduleId((prev) => (prev === taskId ? null : prev))
    if (editingId === taskId) cancelEditing()
    void dispatch(deleteTask(taskId))
  }

  const commitEditing = () => {
    if (!editingId) return
    const trimmed = editingValue.trim()
    const original = tasks.find((t) => t.id === editingId)
    if (!original) {
      cancelEditing()
      return
    }
    if (!trimmed || trimmed === original.title) {
      cancelEditing()
      return
    }
    void dispatch(updateTask({ id: editingId, changes: { title: trimmed } }))
    cancelEditing()
  }

  const count = tasks.length
  const remaining = unscheduledTasks.length + scheduledTasks.length
  const metaLabel = useMemo(() => {
    if (count === 0) return 'Your library is empty — capture your first thought'
    const total = count === 1 ? '1 thought captured' : `${count} thoughts captured`
    return `${total} · ${remaining} still open`
  }, [count, remaining])

  const renderTask = (task: Task) => {
    const scheduleOpen = openScheduleId === task.id
    const priorityOpen = openPriorityId === task.id
    const rowPopoverOpen = scheduleOpen || priorityOpen
    const badge = scheduleLabel(task)
    const isCompleted = !!task.completedAt
    const rowClasses = [styles.thought]
    if (isCompleted) rowClasses.push(styles.thoughtCompleted)
    if (rowPopoverOpen) rowClasses.push(styles.thoughtElevated)
    if (task.priority) rowClasses.push(styles.thoughtHasPriority)
    const priorityLabel = task.priority
      ? `Priority ${task.priority.toUpperCase()}`
      : 'Set priority'
    return (
      <li key={task.id} className={rowClasses.join(' ')}>
        {task.priority ? (
          <span
            className={`${styles.priorityEdge} ${priorityEdgeClass(task.priority)}`}
            aria-hidden
          />
        ) : null}
        <button
          type="button"
          className={`${styles.checkbox} ${isCompleted ? styles.checkboxDone : ''}`}
          onClick={() =>
            void dispatch(
              updateTask({
                id: task.id,
                changes: task.completedAt
                  ? { completedAt: null, status: 'todo' }
                  : { completedAt: new Date().toISOString(), status: 'done' },
              })
            )
          }
          aria-pressed={isCompleted}
          aria-label={
            isCompleted ? `Mark incomplete: ${task.title}` : `Mark complete: ${task.title}`
          }
        >
          {isCompleted ? <CheckIcon /> : null}
        </button>
        {editingId === task.id ? (
          <input
            type="text"
            className={styles.thoughtEditInput}
            value={editingValue}
            autoFocus
            onChange={(e) => setEditingValue(e.target.value)}
            onBlur={commitEditing}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault()
                commitEditing()
              } else if (e.key === 'Escape') {
                e.preventDefault()
                cancelEditing()
              }
            }}
            aria-label={`Edit task title: ${task.title}`}
          />
        ) : (
          <button
            type="button"
            className={styles.thoughtText}
            onClick={() => startEditing(task)}
            title="Edit task title"
          >
            {task.title}
          </button>
        )}
        <div className={styles.thoughtActions}>
          <div className={styles.scheduleAnchor}>
            <button
              type="button"
              className={`${styles.priorityBtn} ${
                task.priority ? styles[`priorityBtn_${task.priority}`] : ''
              }`}
              onClick={() =>
                setOpenPriorityId((prev) => (prev === task.id ? null : task.id))
              }
              aria-label={priorityLabel}
              aria-expanded={priorityOpen}
              title={priorityLabel}
            >
              {task.priority ? (
                <span className={styles.priorityBtnLabel}>
                  {task.priority.toUpperCase()}
                </span>
              ) : (
                <FlagIcon />
              )}
            </button>
            {priorityOpen ? (
              <PriorityPopover
                current={task.priority}
                onPick={(p) => setPriority(task.id, p)}
                onClose={() => setOpenPriorityId(null)}
              />
            ) : null}
          </div>
          {badge.tone === 'empty' ? (
            <button
              type="button"
              className={styles.quickAddBtn}
              onClick={() => quickAddToToday(task.id)}
              aria-label={`Add "${task.title}" to today`}
            >
              Add to today
            </button>
          ) : (
            <span className={`${styles.badge} ${styles[`badge_${badge.tone}`]}`}>
              {badge.text}
            </span>
          )}
          <div className={styles.scheduleAnchor}>
            <button
              type="button"
              className={styles.iconBtn}
              onClick={() =>
                setOpenScheduleId((prev) => (prev === task.id ? null : task.id))
              }
              aria-label="Schedule task"
              aria-expanded={scheduleOpen}
            >
              <CalendarIcon />
            </button>
            {scheduleOpen ? (
              <SchedulePopover
                currentScheduledDate={task.scheduledDate}
                onPick={(dateKey) => reschedule(task.id, dateKey)}
                onUnschedule={() => unschedule(task.id)}
                onClose={() => setOpenScheduleId(null)}
              />
            ) : null}
          </div>
          <button
            type="button"
            className={`${styles.iconBtn} ${styles.iconBtnDanger}`}
            onClick={() => removeTask(task.id, task.title)}
            aria-label={`Delete task: ${task.title}`}
            title="Delete task"
          >
            <TrashIcon />
          </button>
          <button
            type="button"
            className={styles.iconBtn}
            onClick={() => navigate(`/tasks/${task.id}`)}
            aria-label={`View task: ${task.title}`}
            title="View task"
          >
            <EyeIcon />
          </button>
        </div>
      </li>
    )
  }

  const renderSection = (
    title: string,
    tasksInSection: Task[],
    open: boolean,
    onToggle: () => void,
    emptyText: string
  ) => (
    <div className={styles.section}>
      <button
        type="button"
        className={styles.sectionHead}
        onClick={onToggle}
        aria-expanded={open}
      >
        <span className={`${styles.sectionChevron} ${open ? styles.sectionChevronOpen : ''}`}>
          <CaretRight />
        </span>
        <span className={styles.sectionTitle}>{title}</span>
        <span className={styles.sectionCount}>{tasksInSection.length}</span>
      </button>
      {open ? (
        tasksInSection.length === 0 ? (
          <p className={styles.sectionEmpty}>{emptyText}</p>
        ) : (
          <ul className={styles.list}>{tasksInSection.map(renderTask)}</ul>
        )
      ) : null}
    </div>
  )

  return (
    <div className={styles.container}>
      <section className={styles.hero} aria-labelledby="brain-dump-heading">
        <span className={styles.kicker}>Capture your thoughts</span>
        <h1 id="brain-dump-heading" className={styles.title}>
          Clear your mind.
        </h1>
        <div className={styles.captureCard}>
          <span className={styles.editIcon} aria-hidden>
            <EditIcon />
          </span>
          <input
            type="text"
            className={styles.captureInput}
            placeholder="Just write anything..."
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={onKeyDown}
            aria-label="Capture a new thought"
          />
          <button
            type="button"
            className={styles.submitBtn}
            onClick={submit}
            disabled={!draft.trim()}
            aria-label="Capture thought"
          >
            <ArrowRightIcon />
          </button>
        </div>
      </section>

      <section className={styles.inbox} aria-label="Captured thoughts">
        <p className={styles.inboxMeta}>{metaLabel}</p>

        {tasksStatus === 'loading' && tasks.length === 0 ? (
          <SkeletonTaskList count={6} />
        ) : (
          <>
            {renderSection(
              'Brain dump',
              unscheduledTasks,
              unscheduledOpen,
              () => setUnscheduledOpen((v) => !v),
              'Nothing waiting for a home — capture a thought above.'
            )}

            {renderSection(
              'Scheduled',
              scheduledTasks,
              scheduledOpen,
              () => setScheduledOpen((v) => !v),
              'No scheduled tasks yet.'
            )}

            {renderSection(
              'Completed',
              completedTasks,
              completedOpen,
              () => setCompletedOpen((v) => !v),
              'Nothing completed yet.'
            )}
          </>
        )}
      </section>
    </div>
  )
}

export default BrainDump
